import type { CSSProperties } from 'react';

import Typography from '@dynatrace/strato-design-tokens/typography';

import {
  TICK_LINE_HEIGHT,
  TICK_LINE_ICON_OFFSET,
  Y_AXES_TICK_LINE_OFFSET,
} from '../../../../core/components/axes/constants.js';
import type { TickRendererProps } from '../../../../core/components/axes/types.js';

const SMALL_GAP_OFFSET = 1;
const START_ANCHOR_OFFSET = 3;
function getYTickOffset(
  centerTick: boolean,
  renderProps: TickRendererProps,
  tickNameHasEmoji: boolean,
) {
  const TICK_LINE_HEIGHT_VALIDATED = tickNameHasEmoji
    ? TICK_LINE_HEIGHT + TICK_LINE_ICON_OFFSET
    : TICK_LINE_HEIGHT;

  if (centerTick) {
    return TICK_LINE_HEIGHT_VALIDATED / 2 + SMALL_GAP_OFFSET;
  }
  if (renderProps.verticalAnchor === 'start') {
    return START_ANCHOR_OFFSET;
  }
  if (renderProps.verticalAnchor === 'end') {
    return TICK_LINE_HEIGHT_VALIDATED - SMALL_GAP_OFFSET;
  }

  return TICK_LINE_HEIGHT_VALIDATED / 2 + SMALL_GAP_OFFSET;
}

export function useYTickOffset(
  position: string,
  textWidth: number,
  renderProps: TickRendererProps,
  centerTick: boolean,
  tickNameHasEmoji: boolean,
) {
  /**
   * The width to be used by containers in order to make the truncation works.
   * The value should be rounded to avoid a premature truncation in Safari that
   * consider a fractional pixel to truncate.
   **/
  const WIDTH = Math.ceil(textWidth);

  const yOffset = getYTickOffset(centerTick, renderProps, tickNameHasEmoji);

  const xOffset =
    position === 'left'
      ? textWidth + Y_AXES_TICK_LINE_OFFSET - SMALL_GAP_OFFSET
      : -Y_AXES_TICK_LINE_OFFSET + SMALL_GAP_OFFSET;

  const { x, y, ...restProps } = renderProps;

  const style: CSSProperties = {
    ...(restProps as CSSProperties),
    textAlign: position === 'left' ? 'end' : 'start',
    lineHeight: Typography.Text.Base.Default.LineHeight,
    width: WIDTH,
    height: TICK_LINE_HEIGHT,
    overflow: 'visible',
  };
  return { xOffset, yOffset, style };
}
